import { useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import { useAuth } from "@/hooks/auth/useAuth";
import { useMembershipTier } from "@/hooks/useMembershipTier";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { FeedbackForm } from "./FeedbackForm";

export interface FeedbackContext {
  contextType: 'pattern' | 'stitch' | null;
  contextId: string | null;
  contextName: string | null;
  pageUrl: string;
}

/**
 * Floating feedback pill — visible to authenticated, non-admin, beta-tier users.
 *
 * Position:
 *   Desktop  →  bottom: 24px, right: 24px
 *   Mobile   →  bottom: 80px (above the fixed bottom nav bar)
 *
 * z-index 40: above page content, below Shadcn modals (z-50).
 *
 * Context-aware: detects the current page and pre-fills pattern/stitch
 * context when the user is viewing a pattern or stitch.
 */
export function FeedbackButton() {
  const { isAuthenticated, effectiveIsAdmin } = useAuth();
  const { isBeta, isLoading: tierLoading } = useMembershipTier();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Show for logged-in, non-admin (or admin viewing as user), beta-tier users
  if (!isAuthenticated || effectiveIsAdmin || tierLoading || !isBeta) return null;

  // Derive page context for auto-filling the rating form
  const getContext = (): FeedbackContext => {
    const path = location.pathname;
    const pageUrl = path;

    // Pattern pages: /project/:projectId or /projects/:id
    const patternMatch = path.match(/^\/(?:project|projects)\/(\d+|[a-f0-9-]+)/);
    if (patternMatch) {
      return {
        contextType: 'pattern',
        contextId: patternMatch[1],
        contextName: null, // FeedbackForm will resolve the name if needed
        pageUrl,
      };
    }

    // Stitch library detail pages
    const stitchMatch = path.match(/^\/stitch(?:es)?\/(\d+)/);
    if (stitchMatch) {
      return {
        contextType: 'stitch',
        contextId: stitchMatch[1],
        contextName: null,
        pageUrl,
      };
    }

    return { contextType: null, contextId: null, contextName: null, pageUrl };
  };

  const context = getContext();

  const buttonLabel =
    context.contextType === 'pattern' ? 'Rate this pattern' :
    context.contextType === 'stitch'  ? 'Rate this stitch'  :
    'Feedback';

  return (
    <>
      {/* Floating pill button */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Open feedback form"
        className="
          fixed z-40 flex items-center gap-2
          px-4 py-2.5 rounded-full
          text-white text-sm font-medium
          shadow-lg hover:shadow-xl
          transition-shadow duration-200
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2
          /* Desktop */
          bottom-6 right-6
          /* Mobile — sit above the bottom nav (64px) + breathing room */
          max-md:bottom-20
        "
        style={{ background: '#3A3A3A' }}
      >
        <MessageSquare size={15} strokeWidth={2} />
        <span>{buttonLabel}</span>
      </button>

      {/* Feedback dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share Your Feedback</DialogTitle>
            <DialogDescription>
              Your insights help us improve IntelliStitch. Only admins can view submissions.
            </DialogDescription>
          </DialogHeader>
          <FeedbackForm
            onSuccess={() => setOpen(false)}
            context={context}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
